import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from './utils/localization/localization';
import { API_BASE_URL as _API_BASE_URL } from './config';

// Providers
import { AuthProvider } from './contexts/AuthContext';
import { VersionProvider } from './contexts/VersionContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Header from './components/Header';
import ErrorBoundary from './components/ErrorBoundary';
import { useLanguageAwareRender as _useLanguageAwareRender } from './hooks/useLanguageAwareRender';

// Pages
import LoginPage from './pages/LoginPage';
import OidcCallbackPage from './pages/OidcCallbackPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import TranslationTestPage from './pages/TranslationTestPage';
import CVAnalyzerPage from './pages/CVAnalyzerPage';
import JobAnalyzerPage from './pages/JobAnalyzerPage';
import CompanyResearchPage from './pages/CompanyResearchPage';
import ProfilePage from './pages/ProfilePage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import TeamManagementPage from './pages/TeamManagementPage';
import ResultPage from './pages/ResultPage';

// Create a theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2'
    },
    secondary: {
      main: '#dc004e'
    },
    background: {
      default: '#f5f5f5'
    }
  },
  typography: {
    fontFamily: [
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif'
    ].join(',')
  }
});

// This component has access to the language context
function AppContent() {
  const { renderTrigger } = useLanguage();

  return (
    <ErrorBoundary name="AppContent">
      <Header />
      <Container maxWidth={false} disableGutters className="app-main-pane">
        <Box sx={{ my: 4 }}>
          <ErrorBoundary name="Routes">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/callback" element={<OidcCallbackPage />} />
              <Route path="/v1/auth/callback" element={<OidcCallbackPage />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="/translation-test" element={<TranslationTestPage />} />

              {/* Protected routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <CVAnalyzerPage />
                </ProtectedRoute>
              } />
              <Route path="/cv-analyzer" element={
                <ProtectedRoute>
                  <CVAnalyzerPage />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="/job-analyzer" element={
                <ProtectedRoute>
                  <JobAnalyzerPage />
                </ProtectedRoute>
              } />
              <Route path="/company-research" element={
                <ProtectedRoute requiredRole="admin">
                  <CompanyResearchPage />
                </ProtectedRoute>
              } />
              <Route path="/team" element={
                <ProtectedRoute requiredRole={['manager', 'admin']}>
                  <TeamManagementPage />
                </ProtectedRoute>
              } />
              <Route path="/result" element={
                <ProtectedRoute>
                  <ResultPage />
                </ProtectedRoute>
              } />
              <Route path="/result/:userId" element={
                <ProtectedRoute>
                  <ResultPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/*" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboardPage />
                </ProtectedRoute>
              } />

              {/* Unknown route redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ErrorBoundary>
        </Box>
      </Container>
      <Box sx={{ bgcolor: 'background.paper', p: 6 }} component="footer" key={renderTrigger}>
        <Typography variant="body2" color="text.secondary" align="center">
          {new Date().getFullYear()} {t('header.title')} | {t('common.poweredBy')}
        </Typography>
      </Box>
    </ErrorBoundary>
  );
}

function App() {
  useEffect(() => {
    // Listen for force render events
    const handleForceRender = () => {
      // Note: This is now handled by the useLanguageAwareRender hook
      // but keeping this for any other force render events
    };

    window.addEventListener('forceRender', handleForceRender);

    return () => {
      window.removeEventListener('forceRender', handleForceRender);
    };
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary name="App">
        <AuthProvider>
          <VersionProvider>
            <LanguageProvider>
              <Router>
                <AppContent />
              </Router>
            </LanguageProvider>
          </VersionProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
